import { Box, IconButton } from '@material-ui/core';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React from 'react';
import { Link } from 'react-router-dom';

import { Dot } from 'components/table/Dot';
import { Table } from 'components/table/Table';
import { Fund, FundAggregation, FundInvestment, User } from 'libs/api/invest/generated/schema';

import { getValue } from '../../dashboardSettingsUtils';
import { MappedComponent } from '../../dashboardSettingsTypes';
import { useSubFundsPortfolio } from './useSubFundsPortfolio';


export function SubFundsPortfolio(props: {
  components: MappedComponent[],
  currencyConfig: Currency,
  fundAggregation: FundAggregation,
  user: User,
}) {
  const logic = useSubFundsPortfolio();

  function fundRow(fund: Fund, nestingLevel: number) {
    const indent = nestingLevel * 8;
    return [
      ...[
        undefined,
        {
          content:
            <>
              {!logic.expandedSubFunds.includes(fund.id)
                ? <IconButton onClick={() => logic.expandSubFund(fund.id)} style={{padding: 8, marginLeft: indent}}>
                    <ExpandMoreIcon/>
                  </IconButton>
                : <IconButton onClick={() => logic.collapseSubFund(fund.id)} style={{padding: 8, marginLeft: indent}}>
                    <ExpandLessIcon/>
                  </IconButton>
              }
              <Link to={`/fund/${fund.id}/`}>{fund.name}</Link>
            </>
        },
        undefined,
      ],
      ...props.components.map(c => {
        let comp = {...c};
        if (c.name === 'NAV') comp.fieldName = 'bookValue';
        return ['investedCapital', 'NAV'].includes(c.name)
          ? {content: getValue(comp, fund, '', props.currencyConfig)}
          : undefined
      }),
    ]
  }

  function fundInvestmentRow(fi: FundInvestment) {
    return [
      ...[
        {
          content: fi.status === 'A_10'
            ? <div style={{display: 'flex', verticalAlign: 'middle'}}>
                <Dot color="active"/><Box pl={1}>Active</Box>
              </div>
            : <div style={{display: 'flex', verticalAlign: 'middle'}}>
                <Dot color="inactive"/><Box pl={1}>Inactive</Box>
              </div>,
          cellProps: {style: {width: '80px'}},
        },
        {
          content:
            <span style={{marginLeft: 48}}>
              {props.user?.role === 'LP'
                ? fi.company.name
                : <a href={`/company/${fi.company.id}/?aggrId=${fi.fund.id}`}>{fi.company.name}</a>
              }
            </span>,
          cellProps: {style: {width: '20%'}},
        },
      ],
      ...(['PM', 'GP'].includes(props.user?.role || '')
        ? [
            {
              content:
                <div style={{width: '100%', textAlign: "center"}}>
                  <a href={fi.capTableUrl} title="Access Cap Table">
                    <img
                      className="svg-dark-invert"
                      src="/static/icons/icon_external_link.svg"
                      alt="Captable"
                    />
                  </a>
                </div>,
              cellProps: {style: {width: '9%'}},
            }
          ]
        : []),
      ...props.components.map(c => ({content: getValue(c, fi, '', props.currencyConfig)}))
    ]
  }

  return (
    <Table
      columns={[
        ...[
          {label: 'Status', cellProps: {style: {width: '80px'}}, nonSortable: true},
          {label: 'Fund / Company', cellProps: {style: {width: '20%'}}, nonSortable: true},
        ],
        ...(['PM', 'GP'].includes(props.user.role || '')
          ? [{
              label: 'Cap Table',
              cellProps: {style: {width: '9%'}, align: 'center' as 'center'},
              nonSortable: true
            }]
          : []),
        ...props.components.map(c => ({label: c.label, nonSortable: true, tooltip: c.tooltip}))
      ]}
      rows={
        props.fundAggregation.funds
          .filter(f => !!f.subfunds.length)
          .map(f => [
            fundRow(f, 0),
            ...(
              !logic.expandedSubFunds.includes(f.id)
                ? []
                : f.subfunds
                  .map(sf => [
                    fundRow(sf, 1),
                    ...(
                      !logic.expandedSubFunds.includes(sf.id)
                        ? []
                        : sf.fundInvestments.map(fundInvestmentRow)
                    )
                  ])
                  .flat()
            )
          ])
          .flat()
      }
      noSearch
      sectionTitle="Sub Funds Portfolio"
    />
  );
}